import React from 'react'
import { api } from '../api/client'
import type { Billing, Me, PaidRung } from '../api/client'
import { useApp } from '../context/AppContext'

export const POLL_EVERY_MS = 2000
export const POLL_FOR_MS = 60_000

// reached is whether billing shows the rung she paid for: Pro once its
// entitlement is on, card payments once either entitlement carries them.
export function reached(billing: Billing | undefined | null, want: PaidRung): boolean {
  if (!billing) return false
  if (want === 'pro') return billing.entitlements.pro
  return billing.entitlements.card_payments || billing.rung !== 'free'
}

// pollMe asks GET /v1/me every two seconds, for up to a minute, handing each
// answer to onMe, until done says so. It resolves true when done did, false
// when the minute ran out, and stops early (false) once stopped() is true.
export async function pollMe(
  onMe: (me: Me) => void,
  done: (me: Me) => boolean,
  stopped: () => boolean = () => false,
): Promise<boolean> {
  for (let waited = 0; ; waited += POLL_EVERY_MS) {
    try {
      const me = await api.me()
      if (stopped()) return false
      onMe(me)
      if (done(me)) return true
    } catch {
      // A missed answer is retried on the next tick.
    }
    if (stopped() || waited >= POLL_FOR_MS) return false
    await new Promise((resolve) => setTimeout(resolve, POLL_EVERY_MS))
    if (stopped()) return false
  }
}

// overdue is whether to ask her to update her card. The API reports it only
// while the subscription still entitles her.
export function overdue(billing: Billing | null | undefined): boolean {
  return billing?.subscription?.overdue === true
}

export interface BillingView {
  // Her plan, or null from an older API (which had no locks: nothing locks then).
  billing: Billing | null
  // coachLocked and paymentsLocked come from the entitlements alone.
  coachLocked: boolean
  paymentsLocked: boolean
  // refresh reads /v1/me again into the app's state.
  refresh: () => Promise<void>
  // follow polls /v1/me until done, for a change Stripe confirms by webhook.
  follow: (done: (b: Billing | undefined) => boolean) => Promise<boolean>
}

// useBilling is her plan from the session's /v1/me.
export function useBilling(): BillingView {
  const { state, dispatch } = useApp()
  const billing = state.me?.billing ?? null
  const live = React.useRef(true)
  React.useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])
  const update = React.useCallback((me: Me) => dispatch({ type: 'SESSION_STARTED', payload: me }), [dispatch])
  const refresh = React.useCallback(async () => {
    try {
      update(await api.me())
    } catch {
      // The plan shown stays as it was; the next refresh tries again.
    }
  }, [update])
  const follow = React.useCallback(
    (done: (b: Billing | undefined) => boolean) => pollMe(update, (me) => done(me.billing), () => !live.current),
    [update],
  )
  return {
    billing,
    coachLocked: billing !== null && !billing.entitlements.pro,
    paymentsLocked: billing !== null && !billing.entitlements.card_payments,
    refresh,
    follow,
  }
}
